// v4 (V4-U1, spec-v4-extras.md row A3): a video's History fold. The last changes, newest first (by the Director, a
// tap, a note's fix), each with Undo, or Redo while it's undone. Undo is free: it puts back what that one change
// replaced, and whatever was made stays in the library. A change that can't be undone right now says why.
import { useState } from "react";
import { historySummary, loadChanges, rowAction, runUndo, type ChangeItem } from "../changes";
import { useRefresh } from "../store";
import type { Project } from "../types";
import { timeAgo, useLoad } from "./bits";
import { Fold } from "./Fold";

const SHOWN = 8;

export function History({ p }: { p: Project }) {
  const tick = useRefresh("changes", "project");
  const { data } = useLoad(() => loadChanges(p.id), [p.id, tick]);
  const [busy, setBusy] = useState<string | null>(null);
  const [all, setAll] = useState(false);
  const cs: ChangeItem[] = data ?? [];
  if (!cs.length) return null;
  const shown = all ? cs : cs.slice(0, SHOWN);
  const tap = async (c: ChangeItem) => {
    setBusy(c.id);
    await runUndo(c);
    setBusy(null);
  };
  return (
    <div className="history" data-help="project.history">
      <Fold id="project.history" title="History" summary={historySummary(cs)}>
        <ol className="hist-list">
          {shown.map((c) => {
            const act = rowAction(c);
            return (
              <li key={c.id} className={`hist${c.undone ? " is-undone" : ""}`}>
                <div className="hist-text">
                  <p className="hist-label">{c.label}</p>
                  <p className="hist-meta">
                    {timeAgo(c.at)}
                    {c.credits > 0 && <> · {c.credits} credits</>}
                    {c.undone && <> · undone</>}
                  </p>
                  {act.hint && <p className="hist-why">{act.hint}</p>}
                </div>
                <button data-action="history.undo" type="button" className="btn btn-quiet btn-small hist-act" disabled={!act.enabled || busy !== null} aria-label={`${act.label}: ${c.label}`} onClick={() => void tap(c)}>
                  {busy === c.id ? "…" : act.label}
                </button>
              </li>
            );
          })}
        </ol>
        {cs.length > SHOWN && !all && (
          <button type="button" className="btn btn-quiet btn-small" onClick={() => setAll(true)}>
            Show all {cs.length}
          </button>
        )}
        <p className="hist-note">Undo is free. Anything made stays in your library.</p>
      </Fold>
    </div>
  );
}
